import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    Typography,
} from "@mui/material";
import { DialogHeader } from "../../../auth/components/DialogHeader";
import DatePickerLocalizationAdapter from "../../../main/context/DatePickerLocalizationAdapter";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { useContext, useEffect, useState } from "react";
import { usinasApi } from "../../../services/api";
import { MainContext } from "../../../main/context/MainContextProvider";
import { ErrorModal } from "../../../main/components/ErrorModal";
import { useNavigate } from "react-router-dom";
import CardBox from "../../../main/components/CardBox";
import CheckBoxTableRow from "../../users/components/CheckBoxTableRow";
import { Usina } from "../types";

export default function ReprocessDailyReportModal(props: {
    setOpen: (open: boolean) => void;
    open: boolean;
    onMount: () => void;
}) {
    const {
        toastNotifications,
        setToastNotifications,
        relatorioDiarioLoading,
        setRelatorioDiarioLoading,
    } = useContext(MainContext);

    const { open, setOpen, onMount } = props;
    const onClose = () => setOpen(false);

    const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs | null>(dayjs());
    const [errorModalData, setErrorModalData] = useState<{
        title: string;
        message: string;
    }>({ title: "", message: "" });
    const [loading, setLoading] = useState(true);
    const [usinas, setUsinas] = useState<Usina[]>([]);
    const [usinasIds, setUsinasIds] = useState<string[]>([]);

    const navigate = useNavigate();

    const mount = async () => {
        try {
            setLoading(true);
            const response = await usinasApi.getUsinas();
            const dailyUsinas = response.data.filter(
                (usina) => usina.reportOptions?.dailyReportEnabled
            );
            setUsinas(dailyUsinas);
            setUsinasIds(dailyUsinas.map((usina) => usina.usinaIds.id));
        } catch (error) {
            throw error;
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open) {
            mount();
        }
    }, [open]);

    const formatDateParts = (date: dayjs.Dayjs) => {
        const reportMonth = date.month() + 1;
        const dia = date.date() < 10 ? `0${date.date()}` : String(date.date());
        const mes = reportMonth < 10 ? `0${reportMonth}` : String(reportMonth);
        return { dia, mes, ano: date.year() };
    };

    const reprocessRelatoriosDiarios = async () => {
        if (!selectedDate) return;

        const { dia, mes, ano } = formatDateParts(selectedDate);
        setRelatorioDiarioLoading(true);
        setToastNotifications({
            isAdding: true,
            notifications: [
                ...toastNotifications.notifications,
                {
                    severity: "info" as const,
                    text: "O reprocessamento foi iniciado. Você será notificado assim que for finalizado!",
                },
            ],
        });
        onClose();

        try {
            await usinasApi.reprocessDailyReports(
                `${ano}-${mes}-${dia}`,
                usinasIds,
                false
            );
            setToastNotifications({
                isAdding: true,
                notifications: [
                    ...toastNotifications.notifications,
                    {
                        severity: "success" as const,
                        text: (
                            <>
                                Relatórios diários a partir de {`${dia}/${mes}/${ano}`}{" "}
                                foram reprocessados com sucesso!{" "}
                                <a
                                    style={{ cursor: "pointer" }}
                                    onClick={() => navigate("/usinas/relatorios")}
                                >
                                    Clique aqui
                                </a>{" "}
                                para voltar à lista de relatórios.
                            </>
                        ),
                    },
                ],
            });
            onMount();
        } catch (error) {
            console.error(error);
            setToastNotifications({
                isAdding: true,
                notifications: [
                    ...toastNotifications.notifications,
                    {
                        severity: "error" as const,
                        text: "Erro ao reprocessar relatórios diários, tente novamente!",
                    },
                ],
            });
        } finally {
            setRelatorioDiarioLoading(false);
        }
    };

    const closeErrorModal = (openModal: boolean) => {
        !openModal && setErrorModalData({ title: "", message: "" });
    };

    const showErrorModal = errorModalData.title !== "";

    const removeUsinaId = (usinaId: string) => {
        setUsinasIds((prev) => prev.filter((id) => id !== usinaId));
    };

    return (
        <>
            <Dialog
                onClose={onClose}
                aria-labelledby="reprocess-daily-report-dialog"
                open={open}
                sx={{
                    "& .MuiDialog-paper": {
                        width: "100%",
                        maxWidth: "400px",
                        maxHeight: "600px",
                    },
                }}
            >
                <DialogHeader id="reprocess-daily-report-dialog" onClose={onClose}>
                    <Typography
                        sx={{
                            fontSize: {
                                xs: "1rem",
                                sm: "1.5rem",
                            },
                            fontWeight: 500,
                        }}
                    >
                        Regenerar relatório diário
                    </Typography>
                </DialogHeader>
                <DialogContent
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        overflowY: "auto",
                    }}
                    dividers
                >
                    {relatorioDiarioLoading || loading ? (
                        <Box
                            sx={{
                                textAlign: "center",
                                borderBottom: "none",
                                height: "100px",
                            }}
                        >
                            <CircularProgress />
                        </Box>
                    ) : (
                        <>
                            <Typography
                                sx={{
                                    fontWeight: 400,
                                    fontSize: "13px",
                                    marginBottom: "12px",
                                    color: "#CFD8DC",
                                }}
                            >
                                Informe a data em que a energia gerada foi corrigida.
                                Os relatórios diários dessa data até o fim do mês serão
                                reconstruídos (sem enviar notificações).
                            </Typography>
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: { xs: "column", sm: "row" },
                                    columnGap: "10px",
                                    alignItems: "center",
                                    width: "100%",
                                    justifyContent: "space-between",
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontWeight: 500,
                                        fontSize: "16px",
                                    }}
                                >
                                    Usinas
                                </Typography>
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: "row",
                                        alignItems: "center",
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontWeight: 500,
                                            fontSize: "14px",
                                        }}
                                    >
                                        Selecionar Todos
                                    </Typography>
                                    <CheckBoxTableRow
                                        active={usinas.length == usinasIds.length}
                                        onChange={(_, checked) =>
                                            checked
                                                ? setUsinasIds(
                                                      usinas.map((usina) => usina.usinaIds.id)
                                                  )
                                                : setUsinasIds([])
                                        }
                                    />
                                </Box>
                            </Box>
                            <CardBox
                                sx={{
                                    width: "100%",
                                    height: "200px",
                                    maxHeight: "200px",
                                    overflow: "auto",
                                }}
                            >
                                <TableContainer
                                    sx={{
                                        display: "flex",
                                        alignItems: "flex-start",
                                        height: "100%",
                                    }}
                                >
                                    <Table size="small" stickyHeader>
                                        <TableBody>
                                            {usinas.map((usina) => (
                                                <TableRow
                                                    key={usina.usinaIds.id}
                                                    sx={{ display: "flex", width: "100%" }}
                                                >
                                                    <TableCell
                                                        sx={{
                                                            width: "100%",
                                                            display: "flex",
                                                            flexDirection: "row",
                                                            alignItems: "center",
                                                            justifyContent: "space-between",
                                                        }}
                                                        align="left"
                                                    >
                                                        <Typography sx={{ color: "#FFF" }}>
                                                            {usina.name ?? ""}
                                                        </Typography>
                                                        <Box
                                                            sx={{
                                                                display: "flex",
                                                                flexDirection: "row",
                                                                columnGap: "32px",
                                                            }}
                                                        >
                                                            <CheckBoxTableRow
                                                                active={
                                                                    !!usinasIds.find(
                                                                        (item) =>
                                                                            item ===
                                                                            usina.usinaIds.id
                                                                    )
                                                                }
                                                                onChange={(_, checked) => {
                                                                    checked
                                                                        ? setUsinasIds([
                                                                              ...usinasIds,
                                                                              usina.usinaIds.id,
                                                                          ])
                                                                        : removeUsinaId(
                                                                              usina.usinaIds.id
                                                                          );
                                                                }}
                                                            />
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </CardBox>
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    width: "100%",
                                    marginTop: "18px",
                                }}
                            >
                                <DatePickerLocalizationAdapter>
                                    <DatePicker
                                        format="DD/MM/YYYY"
                                        slotProps={{ textField: { size: "small" } }}
                                        maxDate={dayjs(new Date())}
                                        sx={{ marginRight: "8px" }}
                                        label="Data da correção"
                                        value={selectedDate}
                                        onChange={(date) => {
                                            if (!date) return;
                                            setSelectedDate(date);
                                        }}
                                    />
                                </DatePickerLocalizationAdapter>
                            </Box>
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button color="secondary" onClick={onClose}>
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        disabled={
                            relatorioDiarioLoading || loading || usinasIds.length === 0
                        }
                        onClick={reprocessRelatoriosDiarios}
                    >
                        Regenerar
                    </Button>
                </DialogActions>
            </Dialog>
            <ErrorModal
                contentText={errorModalData.message}
                open={showErrorModal}
                setOpen={closeErrorModal}
                title={errorModalData.title}
            />
        </>
    );
}
